import { useEffect, useState } from "react";
import {
  Typography,
  CircularProgress,
  Button,
} from "@mui/material";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import DisabledByDefaultIcon from "@mui/icons-material/DisabledByDefault";
import FilterSortTable from "components/FilterSortTable";

const localInvites = [
  { firstName: "testing", lastName: "123", secretInvite: "true" },
  { firstName: "testing", lastName: "1234", secretInvite: "false" },
  { firstName: "testing", lastName: "12341234", secretInvite: "false" },
  { firstName: "testing", lastName: "456", secretInvite: "false" },
  { firstName: "test", lastName: "test123", secretInvite: "true" },
  { firstName: "a", lastName: "b", secretInvite: "true" },
  { firstName: "a2", lastName: "b2", secretInvite: "true" },
];

const localRSVP = [
  {
    names: ["testing 123", "testing 456"],
    email: "test@test.com",
    rsvp: "true",
    mail_address: "123 100 ave",
    dietary_restrictions: ["na", "veg"],
    sleeping_arrangement_preference: "cabin",
    comments: "",
    submitted: "11/27/2024, 6:54:31 PM",
  },
  {
    names: ["test 1234"],
    email: "test@test.com",
    rsvp: "true",
    mail_address: "123 100 ave",
    dietary_restrictions: ["none"],
    sleeping_arrangement_preference: "cabin",
    comments: "comments",
    submitted: "11/27/2024, 7:00:35 PM",
  },
  {
    names: ["test test123"],
    email: "test@test.com",
    rsvp: "false",
    mail_address: "123",
    dietary_restrictions: ["none"],
    sleeping_arrangement_preference: "tent",
    comments: "com",
    submitted: "11/27/2024, 7:02:20 PM",
  },
];

const localSecretRSVP = [
  {
    names: ["a b", "a2 b2", "test test123"],
    email: "a@b.com",
    secret_rsvp: "true",
    comments: "test",
    submitted: "4/29/2025, 2:57:03 PM",
  },
];

export default function RSVPViewer() {
  const [invites, setInvites] = useState([]);
  const [rsvpData, setRsvpData] = useState([]);
  const [secretRsvpData, setSecretRsvpData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const enumerateRSVP = (rsvp) =>
      rsvp.names.map((name, index) => {
        return {
          name: name,
          email: rsvp.email,
          rsvp: rsvp.rsvp,
          mail_address: rsvp.mail_address,
          dietary_restrictions: rsvp.dietary_restrictions[index],
          sleeping_arrangement_preference: rsvp.sleeping_arrangement_preference,
          comments: rsvp.comments,
          submitted: rsvp.submitted,
        };
      });

    const enumerateSecretRSVP = (rsvp) =>
      rsvp.names.map((name) => {
        return {
          name: name,
          email: rsvp.email,
          secret_rsvp: rsvp.secret_rsvp,
          comments: rsvp.comments,
          submitted: rsvp.submitted,
        };
      });

    switch (process.env.NODE_ENV) {
      case "production":
        const fetchRSVPs = async () => {
          try {
            const [rsvpResponse, secretRsvpResponse] = await Promise.all([
              fetch("/rsvp/list"),
              fetch("/secret_rsvp/list"),
            ]);
            const rsvpJson = await rsvpResponse.json();
            const secretRsvpJson = await secretRsvpResponse.json();
            const enumeratedRsvp = rsvpJson.rsvps
              .map((rsvp) => enumerateRSVP(rsvp))
              .flat();
            const enumeratedSecretRsvp = secretRsvpJson
              .map((rsvp) => enumerateSecretRSVP(rsvp))
              .flat();

            setInvites(rsvpJson.invites);
            setRsvpData(enumeratedRsvp);
            setSecretRsvpData(enumeratedSecretRsvp);
          } catch (error) {
            console.error("Error fetching RSVP data:", error);
          } finally {
            setLoading(false);
          }
        };

        fetchRSVPs();
        break;
      default:
        const enumeratedRsvp = localRSVP
          .map((rsvp) => enumerateRSVP(rsvp))
          .flat();
        const enumeratedSecretRsvp = localSecretRSVP
          .map((rsvp) => enumerateSecretRSVP(rsvp))
          .flat();
        setInvites(localInvites);
        setRsvpData(enumeratedRsvp);
        setSecretRsvpData(enumeratedSecretRsvp);
        setLoading(false);
    }
  }, []);

  const renderTable = (title) => {
    const allNames = new Set();

    // Collect all unique names from invites, rsvpData, and secretRsvpData
    invites.forEach((invite) =>
      allNames.add(
        invite.firstName.trim().toLowerCase() +
          " " +
          invite.lastName.trim().toLowerCase()
      )
    );
    rsvpData.forEach((row) => allNames.add(row.name.trim().toLowerCase()));
    secretRsvpData.forEach((row) =>
      allNames.add(row.name.trim().toLowerCase())
    );

    // Helper to find row by name (case-insensitive)
    const findByName = (data, name) =>
      data.find((row) => row.name && row.name.trim().toLowerCase() === name);

    // Compose columns: name, all rsvpData columns, all secretRsvpData columns (excluding 'names')
    const rsvpColumns =
      rsvpData.length > 0
        ? Object.keys(rsvpData[0]).filter((col) => col !== "name")
        : [];
    const secretColumns =
      secretRsvpData.length > 0
        ? Object.keys(secretRsvpData[0]).filter((col) => col !== "name")
        : [];

    // Compose joined data
    const joinedData = Array.from(allNames)
      .map((name) => {
        const invite = invites.find((invite) => {
          const fullName =
            invite.firstName.trim().toLowerCase() +
            " " +
            invite.lastName.trim().toLowerCase();
          return fullName === name;
        });
        const rsvp = findByName(rsvpData, name) || {};
        const secret = findByName(secretRsvpData, name) || {};
        return {
          name: name,
          secret_invite: invite ? invite.secretInvite : "false",
          ...rsvpColumns.reduce(
            (acc, col) => ({ ...acc, [`rsvp_${col}`]: rsvp[col] || null }),
            {}
          ),
          ...secretColumns.reduce(
            (acc, col) => ({ ...acc, [`secret_${col}`]: secret[col] || null }),
            {}
          ),
        };
      })
      .map((row) => {
        return {
          name: row.name,
          rsvp: row.rsvp_rsvp,
          secret_invite: row.secret_invite,
          secret_rsvp: row.secret_invite === "true" ? row.secret_secret_rsvp : "false",
          email: row.rsvp_email,
          mail_address: row.rsvp_mail_address,
          dietary_restrictions: row.rsvp_dietary_restrictions,
          sleeping_arrangement_preference:
            row.rsvp_sleeping_arrangement_preference,
          comments: row.rsvp_comments,
        };
      });

    const displayBoolean = (value) => {
      if (value === undefined || value === null) {
        return <CheckBoxOutlineBlankIcon color="disabled" />;
      } else if (value === "true") {
        return <CheckBoxIcon color="success" />;
      } else if (value === "false") {
        return <DisabledByDefaultIcon color="error" />;
      }
      return <CheckBoxOutlineBlankIcon color="disabled" />;
    };

    const tableColumns = [
      { id: "name", title: "Name", display_fn: (value) => value.toUpperCase() },
      {
        id: "rsvp",
        title: "RSVP",
        display_fn: (value) => displayBoolean(value),
      },
      {
        id: "secret_invite",
        title: "Secret Invite",
        display_fn: (value) => displayBoolean(value),
      },
      {
        id: "secret_rsvp",
        title: "Secret RSVP",
        display_fn: (value) => displayBoolean(value),
      },
      { id: "email", title: "Email", display_fn: (value) => value || "" },
      {
        id: "mail_address",
        title: "Mail Address",
        display_fn: (value) => value || "",
      },
      {
        id: "dietary_restrictions",
        title: "Dietary Restrictions",
        display_fn: (value) => value || "",
      },
      {
        id: "sleeping_arrangement_preference",
        title: "Sleeping Arrangement Preference",
        display_fn: (value) => value || "",
      },
      { id: "comments", title: "Comments", display_fn: (value) => value || "" },
    ];

    return (
      <>
        <Typography
          variant="h4"
          color="secondary.light"
          align="center"
          gutterBottom
        >
          {title}
        </Typography>
        <FilterSortTable data={joinedData} columns={tableColumns} />
        <div style={{ display: "flex", justifyContent: "center" }}>
          <Button
            onClick={() => {
              // Helper to escape CSV values
              const escapeCSV = (value) => {
                if (value === undefined || value === null) return "";
                let str = value;
                // If value is a React element, try to get its string representation
                if (typeof str === "object" && str !== null && "props" in str) {
                  // Try to get the value from props.children if possible
                  str = str.props && str.props.children
                    ? Array.isArray(str.props.children)
                      ? str.props.children.join("")
                      : str.props.children
                    : "";
                }
                // If value is an array, join with semicolon
                if (Array.isArray(str)) str = str.join("; ");
                str = String(str);
                if (str.includes('"') || str.includes(",") || str.includes("\n")) {
                  str = '"' + str.replace(/"/g, '""') + '"';
                }
                return str;
              };

              const header = tableColumns.map(col => col.id).join(",");
              const rows = joinedData.map(row =>
                tableColumns.map(col => escapeCSV(row[col.id])).join(",")
              );
              const csvContent = [header, ...rows].join("\n");
              const blob = new Blob([csvContent], {
                type: "text/csv;charset=utf-8;",
              });
              const url = URL.createObjectURL(blob);
              const link = document.createElement("a");
              link.href = url;
              link.setAttribute(
                "download",
                `${title.replace(/\s+/g, "_")}.csv`
              );
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);
            }}
            variant="contained"
            color="secondary"
          >
            <Typography variant="body1" color="secondary.light">
              Export to CSV
            </Typography>
          </Button>
        </div>
      </>
    );
  };

  if (loading) {
    return (
      <Typography align="center" variant="h5" color="secondary">
        <CircularProgress /> Loading...
      </Typography>
    );
  }

  return (
    <>
      <Typography
        variant="h3"
        color="secondary.main"
        align="center"
        gutterBottom
      >
        RSVP Viewer
      </Typography>
      {renderTable("RSVP Submissions")}
    </>
  );
}
